import { Box, Typography, Stack, StackProps, ButtonGroup, Button, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import DateInfoTooltip, { DateInfoTooltipProps } from 'components/Tooltip/DateInfoTooltip';
import WidgetMoreActionMenu, {
  MoreOptionMenuProps,
} from 'components/Dropdown/WidgetMoreActionMenu';

import { ReactComponent as ToolTipIcon } from '../../../assets/PepWise2.0/InfoIcon.svg';
import { ReactComponent as MoreBlackIcon } from '../../../assets/PepWise2.0/more-vertical-black.svg';
import { ReactComponent as PlusIcon } from '../../../assets/plus-icon.svg';

type CardActionItem = {
  label: string;
  count?: number | string;
  onBtnClick?: (event?: any) => void;
};

type StatusBadgeLabel = 'AT RISK';

type SectorCardProps = {
  title?: string;
  sectorId?: string;

  bgColor?: string;
  minTitleHeight?: `${number}px`;

  isEditEnabled?: boolean;
  handleEvent?: () => void;
  containerProps?: StackProps;

  infoTooltip?: Omit<DateInfoTooltipProps, 'children'>;
  infoOptionMenu?: MoreOptionMenuProps;

  totalLabel?: string;
  totalCount?: number | string;

  sectionLabel?: string;
  actionItems?: CardActionItem[];

  execTime?: string;
  nextExec?: string;

  statusBadgeLabel?: StatusBadgeLabel;

  emptyStateText?: string;
  addButtonLabel?: string;
  onClickAddItem?: (event: any) => void;

  titleOnClick?: () => void;
};

const SectorCard = ({
  title = '',
  sectorId,
  bgColor,
  minTitleHeight = '45px',
  isEditEnabled,
  handleEvent,
  containerProps,
  infoTooltip,
  infoOptionMenu,
  totalLabel = 'Total Items',
  totalCount,
  sectionLabel = 'Status Overview',
  actionItems = [],
  execTime,
  nextExec,
  statusBadgeLabel,
  emptyStateText = 'No Data',
  addButtonLabel = 'ADD PROJECT',
  onClickAddItem,
  titleOnClick,
}: Readonly<SectorCardProps>) => {
  const theme = useTheme();
  const colors = theme.palette;

  const resolvedBgColor = bgColor ?? colors.widget.background.card;

  const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

  const getActionClassName = (label: string): string => {
    const normalized = label.trim().toLowerCase();

    if (normalized === 'sla missed' || normalized === 'missed') return 'color-missed';
    if (normalized === 'sla met' || normalized === 'met') return 'color-met';
    if (normalized === 'in progress') return 'color-progress';
    if (normalized === 'scheduled') return 'color-scheduled';
    if (normalized === 'at risk') return 'color-risk';

    return 'color-others';
  };

  const getStatusBadgeStyles = (label?: string) => {
    const normalized = label?.trim().toLowerCase();

    if (normalized === 'at risk') {
      return {
        bgColor: colors.widget.sla.others.bg,
        textColor: colors.widget.sla.text,
      };
    }

    return {
      bgColor: colors.widget.sla.scheduled.bg,
      textColor: colors.widget.sla.text,
    };
  };

  const formatToDateObject = (value?: string) => {
    if (!value) return null;

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;

    return null;
  };

  const formatExecDateTime = (value?: string) => {
    if (!value) return '--';

    const date = formatToDateObject(value);
    if (!date) return value;

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${month}-${day}-${year} ${hours}:${minutes}`;
  };

  const formatExecDate = (value?: string) => {
    if (!value) return '--';

    const date = formatToDateObject(value);
    if (!date) return value;

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}-${day}-${year}`;
  };

  const filteredActionItems = actionItems.filter((item) => {
    const countValue = Number(item?.count);

    return hasValue(item?.label) && !Number.isNaN(countValue) && countValue > 0;
  });

  const showExecutionInfo = hasValue(execTime) || hasValue(nextExec);
  const showHeaderActions = Boolean(infoTooltip) || Boolean(infoOptionMenu && isEditEnabled);
  const showTopSummary = Number(totalCount) > 0;
  const showActionItems = Number(totalCount) > 0 && filteredActionItems.length > 0;
  const showEmptyState = Number(totalCount) > 0 && !showActionItems;
  const showAddButton = Boolean(isEditEnabled) && Number(totalCount) === 0;
  const showStatusBadge = hasValue(statusBadgeLabel);

  const badgeStyles = getStatusBadgeStyles(statusBadgeLabel);

  return (
    <Box
      display="flex"
      flexGrow={1}
      height="100%"
      width="100%"
      position="relative"
      {...(handleEvent && {
        onClick: handleEvent,
        sx: { cursor: 'pointer' },
      })}
    >
      <Stack
        minHeight="100px"
        p={2}
        bgcolor={resolvedBgColor}
        fontFamily="Montserrat"
        borderRadius="4px"
        flexGrow={1}
        {...containerProps}
        sx={{
          position: 'relative',
          '&:hover .hover-content': {
            opacity: 1,
          },
          ...containerProps?.sx,
        }}
      >
        <Stack minHeight={minTitleHeight}>
          <Grid container alignItems="flex-start">
            <Grid item xs={10}>
              <Typography
                variant="widgetTitle"
                onClick={titleOnClick}
                sx={{
                  width: '100%',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  cursor: titleOnClick ? 'pointer' : 'default',
                  '& a': { color: colors.widget.text.title },
                }}
              >
                <Link
                  title={title}
                  to={
                    sectorId
                      ? `/data-pipeline/programs/${sectorId}/${encodeURIComponent(title)}`
                      : ''
                  }
                  style={{
                    color: colors.widget.text.title,
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = `${colors.widget.text.action.primary}`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = `${colors.widget.text.title}`)
                  }
                >
                  {title}
                </Link>
              </Typography>
            </Grid>

            {showHeaderActions && (
              <Grid item xs={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  {!!infoTooltip && (
                    <DateInfoTooltip {...infoTooltip}>
                      <ToolTipIcon />
                    </DateInfoTooltip>
                  )}

                  {!!infoOptionMenu && isEditEnabled && (
                    <WidgetMoreActionMenu {...infoOptionMenu}>
                      <MoreBlackIcon />
                    </WidgetMoreActionMenu>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>

          {(showExecutionInfo || showStatusBadge) && (
            <Grid container alignItems="flex-start" mt={2}>
              {showExecutionInfo && (
                <Grid item xs={12} md={showStatusBadge ? 8 : 12}>
                  <Stack spacing={0.35}>
                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                      <Typography
                        variant="widgetBodyLabel"
                        sx={{ color: colors.widget.text.noChange }}
                      >
                        Exec. Time:
                      </Typography>
                      <Typography
                        variant="widgetBodyLabel"
                        sx={{ color: colors.widget.text.value, fontWeight: 400 }}
                      >
                        {hasValue(execTime) ? formatExecDateTime(execTime) : '--'}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                      <Typography
                        variant="widgetBodyLabel"
                        sx={{ color: colors.widget.text.noChange }}
                      >
                        Next Exec.:
                      </Typography>
                      <Typography
                        variant="widgetBodyLabel"
                        sx={{ color: colors.widget.text.value, fontWeight: 400 }}
                      >
                        {hasValue(nextExec) ? formatExecDate(nextExec) : '--'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              )}

              {showStatusBadge && (
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                      alignItems: 'flex-start',
                      mt: { xs: 2, md: 2 },
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.7,
                        borderRadius: '6px',
                        minWidth: '70px',
                        textAlign: 'center',
                        backgroundColor: badgeStyles.bgColor,
                        color: badgeStyles.textColor,
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        cursor: 'default',
                      }}
                    >
                      {statusBadgeLabel}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </Stack>

        <Stack minHeight="20px" flexGrow={1} justifyContent="center" mt={2}>
          {showTopSummary && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              {hasValue(sectionLabel) && (
                <Typography variant="widgetBodyLabel" sx={{ color: colors.widget.text.value }}>
                  {sectionLabel}
                </Typography>
              )}

              {hasValue(totalCount) && (
                <Typography
                  variant="widgetBodyLabelStrong"
                  sx={{ color: colors.widget.text.value, textAlign: 'right' }}
                >
                  {totalLabel} {totalCount}
                </Typography>
              )}
            </Box>
          )}

          {showActionItems && (
            <ButtonGroup
              sx={{
                boxShadow: 'none',
                width: '100%',
                '& button': {
                  flex: 1,
                  borderRadius: '4px',
                  color: colors.widget.sla.text,
                  padding: '5px',
                  overflow: 'hidden',
                  height: '42px',
                  textTransform: 'uppercase',
                  transitionProperty: 'width, opacity',
                  transitionDuration: '0.3s',
                  transitionTimingFunction: 'ease-out, ease-in',
                  transitionDelay: '0s, 0.05s',
                },
                '& .toggle-label': {
                  width: 0,
                  marginLeft: '8px',
                  opacity: 0,
                },
                '&:hover button': {
                  flex: '1 1 0',
                },
                '& button:hover': {
                  width: '150px',
                  flex: '2 1 0',
                },
                '& button:hover .toggle-label': {
                  width: 'auto',
                  transitionProperty: 'width, opacity',
                  transitionDuration: '0.3s',
                  transitionTimingFunction: 'ease-out, ease-in',
                  transitionDelay: '0s, 0.1s',
                  opacity: 1,
                },
                '& .color-missed': {
                  backgroundColor: colors.widget.sla.missed.bg,
                  '&:hover': {
                    backgroundColor: colors.widget.sla.missed.hoverBg,
                  },
                },
                '& .color-met': {
                  backgroundColor: colors.widget.sla.met.bg,
                  '&:hover': {
                    backgroundColor: colors.widget.sla.met.hoverBg,
                  },
                },
                '& .color-scheduled': {
                  backgroundColor: colors.widget.sla.scheduled.bg,
                  '&:hover': {
                    backgroundColor: colors.widget.sla.scheduled.hoverBg,
                  },
                },
                '& .color-progress': {
                  backgroundColor: colors.widget.sla.progress.bg,
                  '&:hover': {
                    backgroundColor: colors.widget.sla.progress.hoverBg,
                  },
                },
                '& .color-risk': {
                  backgroundColor: colors.widget.sla.others.bg,
                  '&:hover': {
                    backgroundColor: colors.widget.sla.others.hoverBg,
                  },
                },
                '& .color-others': {
                  backgroundColor: colors.widget.sla.others.bg,
                  '&:hover': {
                    backgroundColor: colors.widget.sla.others.hoverBg,
                  },
                },
              }}
            >
              {filteredActionItems.map(({ label, count, onBtnClick }) => (
                <Button
                  variant="pepwiseButtonText"
                  className={getActionClassName(label)}
                  key={label}
                  onClick={(event) => {
                    event.stopPropagation();
                    onBtnClick?.(event);
                  }}
                >
                  {count}
                  <span className="toggle-label">{label}</span>
                </Button>
              ))}
            </ButtonGroup>
          )}

          {showEmptyState && (
            <Typography variant="bodyValue" sx={{ color: colors.widget.text.noChange }}>
              {emptyStateText}
            </Typography>
          )}

          {showAddButton && (
            <Button
              variant="pepwiseButtonText"
              startIcon={<PlusIcon />}
              sx={{
                alignSelf: 'flex-start',
                pl: 0,
                pr: 2,
                minWidth: 'auto',
                borderRadius: '8px',
                color: colors.widget.text.action.primary,
                '& .MuiButton-startIcon': {
                  marginRight: '6px',
                  marginLeft: 0,
                },
                '& svg path': {
                  fill: colors.widget.text.action.primary,
                },
              }}
              onClick={(event) => {
                event.stopPropagation();
                onClickAddItem?.(event);
              }}
            >
              {addButtonLabel}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default SectorCard;
